/**
 * A minimal ZIP writer for the print export: stored entries (no
 * compression), UTF-8 names. The package is a few dozen small vector files
 * and one text file, so deflate would save little and cost a dependency.
 * Every unzip tool and both desktop OSes read stored entries.
 */

export interface ZipEntry {
  /** Path inside the archive, "/" separated ("svg/Erika-Mustermann-qr.svg"). */
  name: string;
  data: Uint8Array;
}

const LOCAL_HEADER = 0x04034b50;
const CENTRAL_HEADER = 0x02014b50;
const END_OF_CENTRAL_DIRECTORY = 0x06054b50;
const VERSION = 20; // 2.0: stored entries in folders
const UTF8_NAMES = 0x0800; // general purpose flag bit 11

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

export function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of data) {
    crc = (CRC_TABLE[(crc ^ byte) & 0xff] ?? 0) ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

/** The archive as bytes; `modified` is stamped on every entry (local time, as ZIP has it). */
export function zipBytes(entries: ZipEntry[], modified: Date): Uint8Array<ArrayBuffer> {
  const encoder = new TextEncoder();
  const time =
    (modified.getHours() << 11) | (modified.getMinutes() << 5) | (modified.getSeconds() >> 1);
  const date =
    ((modified.getFullYear() - 1980) << 9) | ((modified.getMonth() + 1) << 5) | modified.getDate();
  const files = entries.map((entry) => ({
    name: encoder.encode(entry.name),
    data: entry.data,
    crc: crc32(entry.data),
  }));
  const localSize = files.reduce((sum, file) => sum + 30 + file.name.length + file.data.length, 0);
  const centralSize = files.reduce((sum, file) => sum + 46 + file.name.length, 0);
  const out = new Uint8Array(localSize + centralSize + 22);
  const view = new DataView(out.buffer);
  let at = 0;
  const u16 = (value: number) => {
    view.setUint16(at, value, true);
    at += 2;
  };
  const u32 = (value: number) => {
    view.setUint32(at, value, true);
    at += 4;
  };
  const bytes = (value: Uint8Array) => {
    out.set(value, at);
    at += value.length;
  };
  // The fields every header repeats: version, flags, method, time, date, crc, sizes, name length.
  const common = (file: (typeof files)[number]) => {
    u16(VERSION);
    u16(UTF8_NAMES);
    u16(0);
    u16(time);
    u16(date);
    u32(file.crc);
    u32(file.data.length);
    u32(file.data.length);
    u16(file.name.length);
  };

  const offsets: number[] = [];
  for (const file of files) {
    offsets.push(at);
    u32(LOCAL_HEADER);
    common(file);
    u16(0); // extra field length
    bytes(file.name);
    bytes(file.data);
  }
  const centralStart = at;
  files.forEach((file, index) => {
    u32(CENTRAL_HEADER);
    u16(VERSION); // made by
    common(file);
    u16(0); // extra field length
    u16(0); // comment length
    u16(0); // disk number
    u16(0); // internal attributes
    u32(0); // external attributes
    u32(offsets[index] ?? 0);
    bytes(file.name);
  });
  const centralLength = at - centralStart;
  u32(END_OF_CENTRAL_DIRECTORY);
  u16(0); // this disk
  u16(0); // disk with the central directory
  u16(files.length);
  u16(files.length);
  u32(centralLength);
  u32(centralStart);
  u16(0); // comment length
  return out;
}
